import { useCallback } from 'react';

import { useSelector } from '@xstate5/react';

import type { PermissionsMachineActorRef } from './permissions.machine';

// ─────────────────────────────────────────────────────────────────────────────

export const usePermissionsMachine = (actorRef: PermissionsMachineActorRef) => {
  //
  // ─── Push flags ──────────────────────────────────────────────────────

  const isPushNotificationPermissionsLoading = useSelector(
    actorRef,
    (state) =>
      state.matches({ 'push-notification-permission': 'waiting' }) ||
      state.matches({ 'push-notification-permission': 'checking-if-enabled' }) ||
      state.matches({ 'push-notification-permission': 'setup' }) ||
      state.matches({ 'push-notification-permission': 'checking-if-suppressed' }),
  );

  const isPushNotificationPrimerVisible = useSelector(actorRef, (state) =>
    state.matches({ 'push-notification-permission': 'showing-primer' }),
  );

  const isRequestingPushNotificationPermission = useSelector(actorRef, (state) =>
    state.matches({ 'push-notification-permission': 'requesting-permission' }),
  );

  const isSkippingPushNotificationPermission = useSelector(actorRef, (state) =>
    state.matches({ 'push-notification-permission': 'skipping' }),
  );

  const isPushNotificationPermissionApproved = useSelector(actorRef, (state) =>
    state.matches({ 'push-notification-permission': 'approved' }),
  );

  const isPushNotificationPermissionDenied = useSelector(actorRef, (state) =>
    state.matches({ 'push-notification-permission': 'denied' }),
  );

  const isPushNotificationPermissionSkipped = useSelector(actorRef, (state) =>
    state.matches({ 'push-notification-permission': 'skipped' }),
  );

  const isPushNotificationPermissionAlreadyShown = useSelector(actorRef, (state) =>
    state.matches({ 'push-notification-permission': 'already-shown' }),
  );

  // ─── Location flags ──────────────────────────────────────────────────

  const isLocationPermissionsLoading = useSelector(
    actorRef,
    (state) =>
      state.matches({ 'location-permission': 'waiting' }) ||
      state.matches({ 'location-permission': 'checking-if-enabled' }) ||
      state.matches({ 'location-permission': 'setup' }) ||
      state.matches({ 'location-permission': 'checking-if-suppressed' }),
  );

  const isLocationPrimerVisible = useSelector(actorRef, (state) =>
    state.matches({ 'location-permission': 'showing-primer' }),
  );

  const isRequestingLocationPermission = useSelector(actorRef, (state) =>
    state.matches({ 'location-permission': 'requesting-permission' }),
  );

  const isSkippingLocationPermission = useSelector(actorRef, (state) =>
    state.matches({ 'location-permission': 'skipping' }),
  );

  const isLocationPermissionApproved = useSelector(actorRef, (state) =>
    state.matches({ 'location-permission': 'approved' }),
  );

  const isLocationPermissionDenied = useSelector(actorRef, (state) =>
    state.matches({ 'location-permission': 'denied' }),
  );

  const isLocationPermissionSkipped = useSelector(actorRef, (state) =>
    state.matches({ 'location-permission': 'skipped' }),
  );

  const isLocationPermissionAlreadyShown = useSelector(actorRef, (state) =>
    state.matches({ 'location-permission': 'already-shown' }),
  );

  // ─── Background location flags ───────────────────────────────────────

  const isBackgroundLocationPermissionsLoading = useSelector(
    actorRef,
    (state) =>
      state.matches({ 'background-location-permission': 'waiting' }) ||
      state.matches({ 'background-location-permission': 'checking-if-enabled' }) ||
      state.matches({ 'background-location-permission': 'setup' }) ||
      state.matches({ 'background-location-permission': 'checking-if-suppressed' }),
  );

  const isBackgroundLocationPrimerVisible = useSelector(actorRef, (state) =>
    state.matches({ 'background-location-permission': 'showing-primer' }),
  );

  const isRequestingBackgroundLocationPermission = useSelector(actorRef, (state) =>
    state.matches({ 'background-location-permission': 'requesting-permission' }),
  );

  const isSkippingBackgroundLocationPermission = useSelector(actorRef, (state) =>
    state.matches({ 'background-location-permission': 'skipping' }),
  );

  const isBackgroundLocationPermissionApproved = useSelector(actorRef, (state) =>
    state.matches({ 'background-location-permission': 'approved' }),
  );

  const isBackgroundLocationPermissionDenied = useSelector(actorRef, (state) =>
    state.matches({ 'background-location-permission': 'denied' }),
  );

  const isBackgroundLocationPermissionSkipped = useSelector(actorRef, (state) =>
    state.matches({ 'background-location-permission': 'skipped' }),
  );

  const isBackgroundLocationPermissionAlreadyShown = useSelector(actorRef, (state) =>
    state.matches({ 'background-location-permission': 'already-shown' }),
  );

  // ─── Handlers ────────────────────────────────────────────────────────

  const requestPushNotificationPermissions = useCallback(() => {
    actorRef.send({ type: 'REQUEST_PUSH_NOTIFICATION_PERMISSIONS' });
  }, [actorRef]);

  const skipPushNotificationPermissions = useCallback(() => {
    actorRef.send({ type: 'SKIP_PUSH_NOTIFICATION_PERMISSIONS' });
  }, [actorRef]);

  const requestLocationPermissions = useCallback(() => {
    actorRef.send({ type: 'REQUEST_LOCATION_PERMISSIONS' });
  }, [actorRef]);

  const skipLocationPermissions = useCallback(() => {
    actorRef.send({ type: 'SKIP_LOCATION_PERMISSIONS' });
  }, [actorRef]);

  const requestBackgroundLocationPermissions = useCallback(() => {
    actorRef.send({ type: 'REQUEST_BACKGROUND_LOCATION_PERMISSIONS' });
  }, [actorRef]);

  const skipBackgroundLocationPermissions = useCallback(() => {
    actorRef.send({ type: 'SKIP_BACKGROUND_LOCATION_PERMISSIONS' });
  }, [actorRef]);

  // ─────────────────────────────────────────────────────────────────────

  return {
    isPushNotificationPermissionsLoading,
    isPushNotificationPrimerVisible,
    isRequestingPushNotificationPermission,
    isSkippingPushNotificationPermission,
    isPushNotificationPermissionApproved,
    isPushNotificationPermissionDenied,
    isPushNotificationPermissionSkipped,
    isPushNotificationPermissionAlreadyShown,
    isLocationPermissionsLoading,
    isLocationPrimerVisible,
    isRequestingLocationPermission,
    isSkippingLocationPermission,
    isLocationPermissionApproved,
    isLocationPermissionDenied,
    isLocationPermissionSkipped,
    isLocationPermissionAlreadyShown,
    isBackgroundLocationPermissionsLoading,
    isBackgroundLocationPrimerVisible,
    isRequestingBackgroundLocationPermission,
    isSkippingBackgroundLocationPermission,
    isBackgroundLocationPermissionApproved,
    isBackgroundLocationPermissionDenied,
    isBackgroundLocationPermissionSkipped,
    isBackgroundLocationPermissionAlreadyShown,
    requestPushNotificationPermissions,
    skipPushNotificationPermissions,
    requestLocationPermissions,
    skipLocationPermissions,
    requestBackgroundLocationPermissions,
    skipBackgroundLocationPermissions,
  };
};
